/* eslint-disable jsdoc/require-param-description */
/* eslint-disable jsdoc/require-param-type */
/* eslint-disable no-use-before-define */
/* eslint-disable prefer-template */
/* eslint-disable react/no-array-index-key */
// #useAuthUpdated
import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";

// Context imports
import _ from "lodash";
import { useCompany } from "../../contexts/CompanyContext";
import { useSB } from "../../contexts/SupabaseContext";
import { useAuth } from "../../contexts/AuthContext";
import { useStructure } from "../../contexts/StructureContext";
import { useUser } from "./hooks/useUser";

// Component imports
import UserDetailHeader from "./components/edit/UserDetailHeader";
import UserDetailContent from "./components/edit/UserDetailContent";
import UserDetailModals from "./components/edit/UserDetailModals";
import { useUserDetailLogic } from "./hooks/useUserDetailLogic";

/**
 * Main UserDetail component - orchestrates the display and management of user information
 */
export default function UserDetail() {
  const history = useHistory();
  const { dealerId } = useParams();
  const { GetTableWhere } = useSB();
  const { fetchOtherDealerStructure } = useStructure();
  const { productTypes, activeSuppliers } = useCompany();
  const { currentUser } = useAuth();
  const { updateUser } = useUser();

  // State management
  const [dealer, setDealer] = useState(null);
  const [parent, setParent] = useState(null);
  const [upStream, setUpstream] = useState([]);
  const [dealerState, setDealerState] = useState(null);
  const [bankingDetails, setBankingDetails] = useState(null);
  const [userSuppliersState, setUserSuppliersState] = useState([]);
  const [userProductTypesState, setUserProductTypesState] = useState([]);

  // Custom hook for business logic
  const {
    // UI State
    editDealerOpen,
    setEditDealerOpen,
    contractsOpen,
    setContractsOpen,
    changeParentOpen,
    setChangeParentOpen,
    changePasswordOpen,
    setChangePasswordOpen,
    suppliersModalOpen,
    setSuppliersModalOpen,
    supportModalOpen,
    setSupportModalOpen,
    creditModalOpen,
    setCreditModalOpen,
    alertModalOpen,
    setAlertModalOpen,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
    deletingDealer,
    setUserTypeModalOpen,
    userTypeModalOpen,

    // Form State
    editTabValue,
    editLoading,
    editError,

    // Status State
    growthLeader,
    isLeader,
    isLeaderBlocked,

    // Actions
    handleGrowthLeaderChange,
    handleIsLeader,
    handleIsLeaderBlock,
    changeLeaver,
    handleDeleteDealer,
    handleEditSubmit,
    handleEditTabChange,
    renderEditTabContent,
  } = useUserDetailLogic({
    dealer,
    dealerId,
    currentUser,
    updateUser,
    bankingDetails,
    refreshDisplayedDetails: async () => {
      setDealer(null);
      const d = await getSubDealerDetails(dealerId);
      setParent(d.parent);
      setDealer(d.dealer);
    },
  });

  /**
   * Retrieves the details of a sub-dealer and its parent dealer.
   * @param subId
   */
  async function getSubDealerDetails(subId) {
    try {
      const dealerData = await GetTableWhere("users_master", [
        "id",
        "eq",
        subId,
      ]);

      if (!dealerData || dealerData.length === 0) {
        throw new Error(`Dealer with ID ${subId} not found`);
      }

      const dealerRecord = dealerData[0];
      setDealerState(dealerRecord);

      // Handle banking detail
      if (dealerRecord.refs_payment_type === "bank") {
        const bankingDetailsData = await GetTableWhere("refs_banking_details", [
          "user_id",
          "eq",
          subId,
        ]);

        if (
          bankingDetailsData &&
          bankingDetailsData.length > 0 &&
          bankingDetailsData[0]
        ) {
          setBankingDetails(bankingDetailsData[0]);
        } else {
          setBankingDetails(null);
        }
      } else {
        setBankingDetails(null);
      }

      // Handle parent data
      if (dealerRecord.parent_id === null) {
        return {
          dealer: dealerRecord,
          parent: null,
        };
      }

      const parentData = await GetTableWhere("users_master", [
        "id",
        "eq",
        dealerRecord.parent_id,
      ]);

      if (!parentData || parentData.length === 0) {
        return {
          dealer: dealerRecord,
          parent: null,
        };
      }

      // Fetch dealer structure
      const dealerStructure = await fetchOtherDealerStructure(subId, true);
      setUpstream(
        _.sortBy(dealerStructure.upstream, "structure_level", "desc")
      );

      return {
        dealer: dealerRecord,
        parent: parentData[0] || null,
      };
    } catch (error) {
      console.error("Error fetching dealer details:", error);
      throw error;
    }
  }

  // Load suppliers and product types
  useEffect(() => {
    const loadSupplierAndProductData = async () => {
      if (!activeSuppliers || !productTypes || !dealerState?.id) {
        return;
      }

      try {
        // Fetch dealer suppliers
        const dealerSuppliers = await GetTableWhere("users_supplier_index", [
          "user_id",
          "eq",
          dealerState.id,
        ]);

        const userSuppliers = activeSuppliers.filter(supplier =>
          dealerSuppliers.find(s => s.supplier_id === supplier.id)
        );

        const supplierNames = userSuppliers
          .map(s => s.supplier_name)
          .join(", ");
        setUserSuppliersState(supplierNames);

        // Fetch dealer product types
        const dealerProductTypes = await GetTableWhere(
          "users_product_type_index",
          ["user_id", "eq", dealerState.id]
        );

        const userProductTypes = productTypes.filter(pt =>
          dealerProductTypes.find(s => s.product_type_id === pt.id)
        );

        const productTypeNames = userProductTypes.map(pt => pt.name).join(", ");
        setUserProductTypesState(productTypeNames);
      } catch (error) {
        console.error("Error loading supplier/product data:", error);
      }
    };

    loadSupplierAndProductData();
  }, [activeSuppliers, productTypes, dealerState]);

  // Initial data load
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const d = await getSubDealerDetails(dealerId);
        setParent(d.parent);
        setDealer(d.dealer);
      } catch (error) {
        console.error("Error loading initial data:", error);
        // Could set an error state here if needed
      }
    };

    if (dealerId) {
      loadInitialData();
    }
  }, [dealerId]);

  if (!dealer) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: "1400px", margin: "0 auto" }}>
      <UserDetailHeader
        dealer={dealer}
        history={history}
        growthLeader={growthLeader}
        isLeader={isLeader}
        userSuppliersState={userSuppliersState}
        userProductTypesState={userProductTypesState}
        onRefresh={async () => {
          setDealer(null);
          const d = await getSubDealerDetails(dealerId);
          setParent(d.parent);
          setDealer(d.dealer);
        }}
      />

      <UserDetailContent
        dealer={dealer}
        bankingDetails={bankingDetails}
        userSuppliersState={userSuppliersState}
        userProductTypesState={userProductTypesState}
        upStream={upStream}
        parent={parent}
        currentUser={currentUser}
        growthLeader={growthLeader}
        isLeader={isLeader}
        isLeaderBlocked={isLeaderBlocked}
        onEditClick={() => setEditDealerOpen(true)}
        onChangeLeaver={changeLeaver}
        onDeleteClick={() => setShowDeleteConfirmation(true)}
        onChangePasswordClick={() => setChangePasswordOpen(true)}
        onContractsClick={() => setContractsOpen(true)}
        onAlertClick={() => setAlertModalOpen(true)}
        onSuppliersClick={() => setSuppliersModalOpen(true)}
        onChangeParentClick={() => setChangeParentOpen(true)}
        onSupportClick={() => setSupportModalOpen(true)}
        onCreditClick={() => setCreditModalOpen(true)}
        onGrowthLeaderChange={handleGrowthLeaderChange}
        onIsLeaderChange={handleIsLeader}
        onIsLeaderBlockChange={handleIsLeaderBlock}
        onUserTypeClick={() => setUserTypeModalOpen(true)}
      />

      <UserDetailModals
        dealer={dealer}
        editDealerOpen={editDealerOpen}
        onCloseEdit={() => setEditDealerOpen(false)}
        editTabValue={editTabValue}
        onEditTabChange={handleEditTabChange}
        editError={editError}
        editLoading={editLoading}
        onEditSubmit={handleEditSubmit}
        renderEditTabContent={renderEditTabContent}
        contractsOpen={contractsOpen}
        onCloseContracts={() => setContractsOpen(false)}
        changePasswordOpen={changePasswordOpen}
        onCloseChangePassword={() => setChangePasswordOpen(false)}
        changeParentOpen={changeParentOpen}
        onCloseChangeParent={() => setChangeParentOpen(false)}
        suppliersModalOpen={suppliersModalOpen}
        onCloseSuppliersModal={() => setSuppliersModalOpen(false)}
        supportModalOpen={supportModalOpen}
        onCloseSupportModal={() => setSupportModalOpen(false)}
        creditModalOpen={creditModalOpen}
        onCloseCreditModal={() => setCreditModalOpen(false)}
        alertModalOpen={alertModalOpen}
        onCloseAlertModal={() => setAlertModalOpen(false)}
        showDeleteConfirmation={showDeleteConfirmation}
        onCloseDeleteConfirmation={() => setShowDeleteConfirmation(false)}
        onConfirmDelete={handleDeleteDealer}
        deletingDealer={deletingDealer}
        onRefreshDetails={async () => {
          setDealer(null);
          const d = await getSubDealerDetails(dealerId);
          setParent(d.parent);
          setDealer(d.dealer);
        }}
        userTypeModalOpen={userTypeModalOpen}
        onCloseUserTypeModal={() => setUserTypeModalOpen(false)}
      />
    </Box>
  );
}
